import React, { createContext, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import './unloadShim'; // must precede sockjs-client
import SockJS from 'sockjs-client';

const WebSocketContext = createContext();

// The signed-in app's live client, for announceLogout() — logout handlers live outside the
// provider's context consumers (and some run as the tree is being torn down).
let activeClient = null;

// Tells the server this is a real logout, so its "has left the chat" line goes out at once
// instead of after the grace period PresenceService gives a dropped socket to reconnect.
// Presence itself is server-driven from the session: JOIN on connect, LEAVE on disconnect.
export function announceLogout() {
    try {
        if (activeClient?.connected) activeClient.publish({destination: '/app/presence/logout', body: ''});
    } catch (e) { /* the disconnect still produces the LEAVE, just after the grace period */ }
}

const WebSocketProvider = ({ children }) => {
    const [stompClient, setStompClient] = useState(null);
    const userJSON = JSON.parse(sessionStorage.getItem('user'));
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        const client = new Client({
            // Do NOT restrict SockJS transports here — native WebSocket can't complete
            // its handshake against production (api.dropwyrm.com), so forcing
            // 'websocket'-only leaves the client stuck reconnecting forever. The HTTP
            // fallback transports (xhr-streaming/xhr-polling/eventsource) are load-bearing.
            // Those fallbacks DO have a real bug though: they split response bodies using
            // a byte-length header checked against a UTF-16 string index, which corrupts
            // frames whenever a payload contains multi-byte UTF-8 characters (accents,
            // curly quotes, emoji) — see the ASCII-escaping in the send() shim below,
            // which is the actual fix for that.
            webSocketFactory: () => new SockJS(`${process.env.REACT_APP_WS_PROXY}`),
            connectHeaders: { Authorization: 'Bearer ' + userJSON.accessToken },
            // Broadcasts (e.g. TOKEN_MOVE) sent while disconnected are dropped with no
            // retry/queue — a shorter delay here is the main lever for how long other
            // clients can see stale state after a transient WS drop.
            reconnectDelay: 1500,
            heartbeatIncoming: 20000,
            heartbeatOutgoing: 20000,
            debug: () => {},
            onConnect: () => {
                if (!mountedRef.current) return;
                // Shim send() so existing callers don't need to change.
                if (!client.send) {
                    // Escape non-ASCII characters to \uXXXX before publishing. Keeping the
                    // wire payload pure ASCII means byte length always equals UTF-16 length,
                    // which sidesteps the SockJS HTTP-fallback chunking bug above regardless
                    // of which transport a given client ends up negotiating.
                    const toAsciiSafe = (body) => body.replace(/[^\x00-\x7f]/g, (c) =>
                        '\\u' + c.charCodeAt(0).toString(16).padStart(4, '0'));
                    client.send = (destination, headers, body) =>
                        client.publish({
                            destination,
                            headers: headers || {},
                            body: typeof body === 'string' ? toAsciiSafe(body) : (body || ''),
                        });
                }
                activeClient = client;
                setStompClient(client);
            },
            onWebSocketClose: () => {
                if (!mountedRef.current) return;
                setStompClient(null);
            },
            onStompError: (frame) => {
                console.error('STOMP error:', frame.headers?.message);
            },
        });

        client.activate();

        return () => {
            mountedRef.current = false;
            if (activeClient === client) activeClient = null;
            setStompClient(null);
            client.deactivate();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <WebSocketContext.Provider value={{ stompClient }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export { WebSocketContext, WebSocketProvider };
